import { addDays } from 'date-fns';

import {
  httpGetJobsByJobId,
  httpPatchJobsByJobId,
  httpPutJobsPriority,
  type JobsJobElasticSchemaWithId,
  type JobsJobElasticSchemaWithIdNullable,
  type JobsJobSchemaPartial,
  type JobsJobSchemaWithIdNullable,
} from '@iconik-io/iconik-api/src/services/jobs';
import type { WsJobEntity } from '@iconik-io/shared-lib/src/react/components/shared/websocket_manager/websocket_notification_types';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';
import { handleErrors } from '@iconik-io/shared-lib/src/utils/handle_messages';

import {
  JOB_STATUS_FAILED,
  JOB_STATUS_STARTED,
  JOB_STATUS_WAITING,
  type JobStatus,
} from '../../constants/jobs';
import {
  ADMIN_JOBS_HISTORY_PAGE_ROUTE,
  ADMIN_JOBS_HISTORY_SUBJOBS_PAGE_ROUTE,
} from '../../constants/routes';
import type { AppDispatch, AppState, GetState } from '../../store/types';
import type { AnyObject } from '../../types/common';
import { commonListActions } from '../common_list/slice';
import { getJobWidgetsCommonListPrefix } from '../common_list/utils';
import { setJobWidgetStats } from '../jobs_stats_widgets/slice';
import type { JobsStats } from '../jobs_stats_widgets/types';
import { selectWidgets } from '../jobs_widgets/selectors';
import { errorMessage } from '../messages/thunks';

const IDLE_BEFORE_DELETE = 1000;

const isNonEmptyArray = (value: any) =>
  Array.isArray(value) && value.length > 0;

const isJobMatchFilters = (
  job: JobsJobElasticSchemaWithIdNullable,
  filters: AnyObject,
  state: AppState,
  options: { skipStatusCheck?: boolean } = {},
): boolean => {
  if (filters.my_jobs) {
    if (state.authReducer.userData.id !== job.created_by) {
      return false;
    }
  }

  if (isNonEmptyArray(filters.created_by)) {
    if (!job.created_by || !filters.created_by.includes(job.created_by)) {
      return false;
    }
  }

  if (!options.skipStatusCheck && isNonEmptyArray(filters.status)) {
    if (!filters.status.includes(job.status)) {
      return false;
    }
  }

  if (isNonEmptyArray(filters.object_type)) {
    if (!job.object_type || !filters.object_type.includes(job.object_type)) {
      return false;
    }
  }

  if (isNonEmptyArray(filters.custom_type)) {
    if (!filters.custom_type.includes(job.type)) {
      return false;
    }
  }

  if (isNonEmptyArray(filters.type)) {
    if (!filters.type.includes(job.type)) {
      return false;
    }
  }

  if (filters.date_created) {
    if (!job.date_created) {
      return false;
    }

    const from = new Date(filters.date_created.min).getTime();
    const to = filters.date_created.max
      ? new Date(filters.date_created.max).getTime()
      : addDays(new Date(from), 1).getTime();
    const jobCreated = new Date(job.date_created).getTime();
    if (jobCreated < from || jobCreated > to) {
      return false;
    }
  }

  if (filters.date_modified) {
    if (!job.date_modified) {
      return false;
    }
    const from = new Date(filters.date_modified.min).getTime();
    const to = filters.date_modified.max
      ? new Date(filters.date_modified.max).getTime()
      : addDays(new Date(from), 1).getTime();
    const jobCreated = new Date(job.date_modified).getTime();
    if (jobCreated < from || jobCreated > to) {
      return false;
    }
  }

  return true;
};

const statusKeyMap: {
  [status in JobStatus]?: 'active' | 'waiting' | 'failed';
} = {
  [JOB_STATUS_STARTED]: 'active',
  [JOB_STATUS_WAITING]: 'waiting',
  [JOB_STATUS_FAILED]: 'failed',
};

const updateStats =
  (
    job: JobsJobElasticSchemaWithIdNullable,
    prevJob?: JobsJobElasticSchemaWithId,
  ) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();

    const widgets = selectWidgets(state).filter(
      (widget) => widget.type === 'JOBS_STATS_LIST',
    );
    widgets.forEach((widget) => {
      const stats = state.jobsStatsWidgets[widget.id];
      const prevJobIncluded = Boolean(
        prevJob &&
          isJobMatchFilters(prevJob, widget.options.filters, state, {
            skipStatusCheck: true,
          }),
      );
      const jobIncluded = isJobMatchFilters(
        job,
        widget.options.filters,
        state,
        { skipStatusCheck: true },
      );
      const newStats: JobsStats = { ...stats };
      let updated = false;
      if (prevJob?.status !== job.status) {
        if (prevJob) {
          const prevStatKey = statusKeyMap[prevJob.status];
          if (prevStatKey && newStats[prevStatKey] >= 1) {
            newStats[prevStatKey] -= 1;
            updated = true;
          }
        }
        const statKey = statusKeyMap[job.status];
        if (statKey) {
          newStats[statKey] += 1;
          updated = true;
        }
      }
      if (!prevJobIncluded && jobIncluded) {
        newStats.total += 1;
        updated = true;
      }
      if (prevJobIncluded && !jobIncluded && newStats.total >= 1) {
        newStats.total -= 1;
        updated = true;
      }
      if (updated) {
        dispatch(
          setJobWidgetStats({
            widgetId: widget.id,
            stats: newStats,
          }),
        );
      }
    });
  };

const distributeWidgetJob =
  (job: JobsJobSchemaWithIdNullable, prevJob?: JobsJobElasticSchemaWithId) =>
  (dispatch: AppDispatch, getState: GetState) => {
    // skip subjobs in widgets
    if (job.parent_id) {
      return;
    }

    const state = getState();

    const widgets = selectWidgets(state);
    widgets.forEach((widget) => {
      const { prependId: widgetPrependId, deleteId: widgetDeleteId } =
        commonListActions(getJobWidgetsCommonListPrefix(widget.id));
      const prevJobIncluded = Boolean(
        prevJob && isJobMatchFilters(prevJob, widget.options.filters, state),
      );
      const jobIncluded = isJobMatchFilters(job, widget.options.filters, state);
      if (!prevJobIncluded && jobIncluded) {
        dispatch(widgetPrependId(job.id));
      }
      if (prevJobIncluded && !jobIncluded) {
        // wait before removing job from the widget
        window.setTimeout(() => {
          dispatch(widgetDeleteId(job.id));
        }, IDLE_BEFORE_DELETE);
      }
    });
  };

const { prependId: historyPrependId } = commonListActions('admin_jobs_history');

const distributeHistoryJob =
  (job: JobsJobSchemaWithIdNullable) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const { route, params } = state.routeParams;
    const { filterValues, facetValues } = state.panelsState.adminJobsFilters;
    const jobIncluded = isJobMatchFilters(
      job,
      {
        ...filterValues,
        ...facetValues,
        custom_type: facetValues.type,
      },
      state,
    );
    if (
      route === ADMIN_JOBS_HISTORY_PAGE_ROUTE &&
      !job.parent_id &&
      jobIncluded
    ) {
      dispatch(historyPrependId(job.id));
    } else if (
      route === ADMIN_JOBS_HISTORY_SUBJOBS_PAGE_ROUTE &&
      job.parent_id === params.id &&
      jobIncluded
    ) {
      dispatch(historyPrependId(job.id));
    }
  };

export const handleWebsocketMessages =
  (data: WsJobEntity) => (dispatch: AppDispatch, getState: GetState) => {
    const objectId = data.object_id;
    if (!objectId) {
      return;
    }
    if (data.operation === 'update' && data.data) {
      const job = data.data;
      const state = getState();
      const prevJob = state.entities.jobs[job.id];
      dispatch(updateEntities({ jobs: { [job.id]: job } }));
      dispatch(distributeWidgetJob(job, prevJob));
      dispatch(updateStats(job, prevJob));
    }
    if (data.operation === 'create' && data.realm === 'entity') {
      dispatch(httpGetJobsByJobId(objectId)).then((response) => {
        const job = response.body;

        dispatch(distributeHistoryJob(job));
        dispatch(distributeWidgetJob(job));
        dispatch(updateStats(job));
      });
    }
  };

export const setJobPriority =
  (jobId: string, priority: number) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const job = getState().entities.jobs[jobId];
    return dispatch(
      httpPutJobsPriority({
        job_ids: [jobId],
        priority,
      }),
    )
      .then(() => {
        dispatch(
          updateEntities({
            jobs: {
              [job.id]: {
                ...job,
                priority,
              },
            },
          }),
        );
      })
      .catch((error) => {
        dispatch(errorMessage({ text: handleErrors(error) }));
        throw error;
      });
  };

export const updateJob =
  (jobId: string, data: JobsJobSchemaPartial) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const writeJobs = getState().rolesReducer.userRoles.canWriteJobs;
    if (writeJobs && jobId) {
      dispatch(httpPatchJobsByJobId(jobId, data));
    }
  };
